import React from 'react';
import { useNavigate } from 'react-router';

const PricingInsuranceSection = () => {
  const navigate = useNavigate();

  return (
    <div className="mx-auto px-4 md:px-8 lg:px-20 py-16 md:py-24">
      <div className="flex flex-col md:flex-row rounded-3xl overflow-hidden shadow-xl transition-all duration-300 hover:shadow-2xl">
        {/* Left Section - Pricing */}
        <div className="w-full md:w-1/2 p-8 md:p-12 bg-white relative overflow-hidden">
          {/* Background SVG Pattern */}
          <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none">
            <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
              <path d="M0,0 L100,0 L100,100 Z" className="fill-teal-500" />
              <circle cx="80" cy="20" r="30" className="fill-lime-500" />
            </svg>
          </div>

          <div className="relative max-w-md">
            {/* Price Tag Icon - Using Tailwind colors */}
            <div className="mb-6">
              <svg 
                className="w-12 h-12" 
                viewBox="0 0 24 24" 
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" className="fill-teal-500 opacity-20" />
                <path d="M12 20c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5H10.9v1.69c-1.51.32-2.72 1.3-2.72 2.81 0 1.79 1.49 2.69 3.66 3.21 1.95.46 2.34 1.15 2.34 1.87 0 .53-.39 1.39-2.1 1.39-1.6 0-2.23-.72-2.32-1.64H8.04c.1 1.7 1.36 2.66 2.86 2.97V19h2.34v-1.67c1.52-.29 2.72-1.16 2.73-2.77-.01-2.2-1.9-2.96-3.66-3.42z" className="fill-teal-500" />
              </svg>
            </div>

            <p className="uppercase tracking-wider font-medium mb-4 text-lime-500">
              TRANSPARENT PRICING
            </p>

            <h2 className="text-3xl md:text-4xl font-serif text-slate-800 mb-6 relative">
              No Surprises
              <span className="absolute -bottom-2 left-0 w-16 h-1 rounded-full bg-teal-500"></span>
            </h2>

            <p className="text-slate-600 leading-relaxed">
              Our team believes in accessible dental care. We offer straightforward pricing, clear estimates, and an unprecedented dental warranty. Keep up with your routine 6-month checkups and if something needs fixing, we'll handle it at no cost to you.
            </p>
            
            {/* Animated button on hover */}
            <button onClick={() => navigate('/service-fee')} className="cursor-pointer mt-8 px-6 py-3 rounded-full font-medium text-white transition-all duration-300 hover:shadow-lg flex items-center group bg-teal-500 hover:bg-teal-600">
              View Price List
              <svg 
                className="ml-2 w-5 h-5 transform transition-transform duration-300 group-hover:translate-x-1" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>
        </div>

        {/* Right Section - Insurance */}
        <div className="w-full md:w-1/2 p-8 md:p-12 text-white relative overflow-hidden bg-gradient-to-br from-lime-500 to-teal-500">
          {/* Background Pattern */}
          <div className="absolute top-0 right-0 w-full h-full opacity-10 pointer-events-none">
            <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
              <path d="M100,0 L0,100 L100,100 Z" fill="white" />
              <circle cx="20" cy="20" r="30" fill="white" />
            </svg>
          </div>
          
          <div className="relative max-w-md ml-auto">
            <div className="mb-6">
              <svg 
                className="w-12 h-12" 
                viewBox="0 0 24 24" 
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect x="3" y="5" width="18" height="14" rx="2" fill="white" opacity="0.2" />
                <rect x="7" y="9" width="4" height="3" rx="1" stroke="white" strokeWidth="1.5" fill="none" />
                <path d="M14 9H18" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M14 12H18" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M7 15H18" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>

            <h2 className="text-3xl md:text-4xl font-serif mb-6 relative">
              Insurance
              <span className="absolute -bottom-2 left-0 w-16 h-1 bg-white rounded-full"></span>
            </h2>

            <p className="leading-relaxed mb-8">
              Our office is in-network with Delta Premier and bills all other providers. Our team will explain how your specific dental benefits apply to the cost of treatment.
            </p>

            {/* Insurance Card with Animation */}
            <div className="w-48 h-32 relative perspective-500 transform transition-transform duration-500 hover:rotate-y-10 hover:scale-105">
              <div className="absolute inset-0 rounded-xl shadow-md bg-gradient-to-br from-white to-gray-100">
                <div className="absolute top-0 left-0 w-full h-full rounded-xl overflow-hidden">
                  <div className="absolute top-0 left-0 w-16 h-16 rounded-br-3xl bg-teal-500"></div>
                  <div className="absolute bottom-0 right-0 w-32 h-6 rounded-tl-lg bg-lime-500 opacity-70"></div>
                  
                  {/* Card Content */}
                  <div className="absolute inset-0 p-4 flex flex-col justify-between">
                    <div className="text-xs font-medium text-gray-600">DENTAL INSURANCE</div>
                    <div className="mt-6 ml-2">
                      <div className="w-10 h-6 rounded bg-gray-200 mb-1"></div>
                      <div className="w-16 h-1 rounded bg-gray-300"></div>
                    </div>
                    <div className="flex justify-between items-end">
                      <div className="text-xs text-gray-500">MEMBER</div>
                      <div className="text-xs text-teal-500">PRISTINE SMILE</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* List of accepted insurances */}
            <div className="mt-6">
              <div className="flex flex-wrap gap-2 mt-4">
                {['Delta', 'Cigna', 'Aetna', 'MetLife'].map((name) => (
                  <span key={name} className="px-3 py-1 rounded-full text-sm bg-white text-gray-800 opacity-90">{name}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingInsuranceSection;